import type { ApiResponse } from 'types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1';

export const TOKEN_KEY = 'tp_admin_token';
export const ADMIN_KEY = 'tp_admin';

const getToken = () => localStorage.getItem(TOKEN_KEY);

/** Collapse concurrent identical GETs (e.g. React StrictMode double-mount in dev). */
const inflightGetRequests = new Map<string, Promise<ApiResponse<unknown>>>();

const buildHeaders = (hasBody: boolean): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (hasBody) headers['Content-Type'] = 'application/json';
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

/**
 * A 401 means the session is missing/expired/invalid. Clear it and bounce to
 * login — unless we're already there (e.g. a failed login attempt).
 */
const handleUnauthorized = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ADMIN_KEY);
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

/**
 * Generic HTTP request handler with typed responses
 * @template T - Response data type
 * @param method - HTTP method
 * @param endpoint - API endpoint path
 * @param body - Optional request body
 * @returns Typed API response
 */
const request = async <T = any>(method: string, endpoint: string, body?: unknown): Promise<ApiResponse<T>> => {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: buildHeaders(body !== undefined),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    if (response.status === 401) {
      handleUnauthorized();
    }

    return (await response.json()) as ApiResponse<T>;
  } catch (error) {
    console.error(`API ${method} ${endpoint} Error:`, error);
    throw error;
  }
};

export const api = {
  /**
   * GET request
   * @template T - Response data type
   */
  get: <T = unknown>(endpoint: string) => {
    const key = `GET:${endpoint}`;
    const existing = inflightGetRequests.get(key);
    if (existing) return existing as Promise<ApiResponse<T>>;

    const promise = request<T>('GET', endpoint).finally(() => {
      inflightGetRequests.delete(key);
    });
    inflightGetRequests.set(key, promise as Promise<ApiResponse<unknown>>);
    return promise;
  },

  /**
   * POST request
   * @template T - Response data type
   */
  post: <T = unknown>(endpoint: string, body: unknown) => request<T>('POST', endpoint, body),

  /**
   * PUT request
   * @template T - Response data type
   */
  put: <T = unknown>(endpoint: string, body: unknown) => request<T>('PUT', endpoint, body),
  delete: <T = unknown>(endpoint: string) => request<T>('DELETE', endpoint),
};
